import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClerk, useUser } from '@clerk/react';
import { useTranslation } from '@yorukiri/i18n';
import { Button, Select } from '@yorukiri/ui';
import { colors } from '@yorukiri/ui/tokens';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { Slider } from '../components/Slider';
import { StepPills } from '../components/StepPills';
import { Stepper } from '../components/Stepper';
import {
  type ApiDeckConfig,
  type ApiPageSummary,
  useBillingMe,
  useBillingPlans,
  useCheckout,
  useDeckConfig,
  usePages,
  useSubscription,
  useUpdateDeckConfig,
} from '../lib/queries';

// Single-page scroll layout. Sticky left nav jumps to sections; content is a
// vertical stack. Matches the general aesthetic of the library + review home.
export function SettingsRoute() {
  const { t } = useTranslation('settings');
  const sections: { id: string; label: string }[] = [
    { id: 'profile', label: t('section_profile') },
    { id: 'general', label: t('section_general') },
    { id: 'review', label: t('section_review') },
    { id: 'billing', label: t('section_billing') },
    { id: 'shortcuts', label: t('section_shortcuts') },
    { id: 'account', label: t('section_account') },
  ];
  const [active, setActive] = useState('profile');

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '200px 1fr',
        gap: 48,
        maxWidth: 1000,
        padding: '32px 40px 80px',
      }}
    >
      <nav
        style={{
          position: 'sticky',
          top: 32,
          alignSelf: 'start',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', marginBottom: 16 }}>
          {t('title')}
        </h1>
        {sections.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            onClick={() => setActive(s.id)}
            style={navLink(active === s.id)}
          >
            {s.label}
          </a>
        ))}
      </nav>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <ProfileSection />
        <GeneralSection />
        <ReviewSection />
        <BillingSection />
        <ShortcutsSection />
        <AccountSection />
      </div>
    </div>
  );
}

function Section({
  id,
  title,
  description,
  children,
}: {
  id: string;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} style={{ scrollMarginTop: 40 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.015em', margin: 0 }}>
        {title}
      </h2>
      {description && (
        <p style={{ color: colors.ink3, fontSize: 13, marginTop: 4, marginBottom: 20 }}>
          {description}
        </p>
      )}
      <div
        style={{
          marginTop: description ? 0 : 16,
          background: colors.surface2,
          border: `1px solid ${colors.line}`,
          borderRadius: 10,
          padding: '20px 22px',
        }}
      >
        {children}
      </div>
    </section>
  );
}

function ProfileSection() {
  const { t } = useTranslation('settings');
  const { user } = useUser();
  const clerk = useClerk();
  const name = user?.fullName ?? '';
  const email = user?.primaryEmailAddress?.emailAddress ?? '';

  return (
    <Section id="profile" title={t('profile_title')} description={t('profile_desc')}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: user?.imageUrl ? `url(${user.imageUrl}) center/cover` : colors.line2,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 16 }}>{name || email}</div>
          <div style={{ color: colors.ink3, fontSize: 12.5, marginTop: 2 }}>{email}</div>
        </div>
        <Button variant="secondary" onClick={() => clerk.openUserProfile()}>
          {t('manage_account')}
        </Button>
      </div>
    </Section>
  );
}

function GeneralSection() {
  const { t } = useTranslation('settings');
  return (
    <Section id="general" title={t('general_title')}>
      <Row
        label={t('language')}
        description={t('language_desc')}
        right={<LanguageSwitcher />}
      />
    </Section>
  );
}

function ReviewSection() {
  const { t } = useTranslation('settings');
  const pagesQuery = usePages();
  // Only root-level pages get their own deck config (the scheduler walks up to
  // the root anyway). Pick the first root by default.
  const rootPages = useMemo(
    () => (pagesQuery.data ?? []).filter((p) => p.parentPageId === null),
    [pagesQuery.data],
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const activeId = selectedId ?? rootPages[0]?.id ?? null;

  return (
    <Section
      id="review"
      title={t('review_title')}
      description={t('review_desc')}
    >
      <Row
        label={t('deck')}
        description={t('deck_desc')}
        right={
          rootPages.length === 0 ? (
            <span style={{ color: colors.ink3, fontSize: 13 }}>{t('no_decks')}</span>
          ) : (
            <Select
              size="sm"
              value={activeId ?? undefined}
              onValueChange={(v) => setSelectedId(v)}
              options={rootPages.map((p) => ({ value: p.id, label: p.title }))}
              triggerStyle={{ minWidth: 200 }}
            />
          )
        }
      />
      {activeId && <DeckConfigForm pageId={activeId} page={rootPages.find((p) => p.id === activeId)} />}
    </Section>
  );
}

function DeckConfigForm({ pageId, page }: { pageId: string; page?: ApiPageSummary }) {
  const { t } = useTranslation('settings');
  const configQuery = useDeckConfig(pageId);
  const updateConfig = useUpdateDeckConfig();
  const [draft, setDraft] = useState<Partial<ApiDeckConfig> | null>(null);

  const cfg = configQuery.data;
  const effective = { ...(cfg ?? defaults), ...(draft ?? {}) } as ApiDeckConfig;
  const dirty = draft !== null && Object.keys(draft).length > 0;

  if (!cfg) {
    return (
      <div style={{ color: colors.ink3, fontSize: 13, padding: '8px 0' }}>…</div>
    );
  }

  const set = <K extends keyof ApiDeckConfig>(k: K, v: ApiDeckConfig[K]) =>
    setDraft((d) => ({ ...(d ?? {}), [k]: v }));

  const save = async () => {
    if (!dirty) return;
    await updateConfig.mutateAsync({ pageId, patch: draft ?? {} });
    setDraft(null);
  };

  return (
    <div
      style={{
        marginTop: 16,
        paddingTop: 20,
        borderTop: `1px solid ${colors.line}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
      }}
    >
      <div style={{ fontSize: 12, color: colors.ink3 }}>
        {page?.title ?? ''}
      </div>

      <Row
        label={t('scheduler')}
        description={t('scheduler_desc')}
        right={
          <Select<'fsrs' | 'sm2'>
            size="sm"
            value={effective.scheduler}
            onValueChange={(v) => set('scheduler', v)}
            options={[
              { value: 'fsrs', label: 'FSRS', description: t('scheduler_fsrs_hint') },
              { value: 'sm2', label: 'SM-2', description: t('scheduler_sm2_hint') },
            ]}
            triggerStyle={{ minWidth: 140 }}
          />
        }
      />

      <Row
        label={t('retention')}
        description={t('retention_desc')}
        right={
          <Slider
            value={Math.round(effective.fsrsRetention * 100)}
            min={70}
            max={99}
            step={1}
            suffix="%"
            onChange={(v) => set('fsrsRetention', v / 100)}
          />
        }
      />

      <Row
        label={t('new_per_day')}
        description={t('new_per_day_desc')}
        right={
          <Stepper
            value={effective.maxNewPerDay}
            min={0}
            max={9999}
            onChange={(v) => set('maxNewPerDay', v)}
          />
        }
      />

      <Row
        label={t('reviews_per_day')}
        description={t('reviews_per_day_desc')}
        right={
          <Stepper
            value={effective.maxReviewsPerDay}
            min={0}
            max={99999}
            onChange={(v) => set('maxReviewsPerDay', v)}
          />
        }
      />

      <Row
        label={t('learning_steps')}
        description={t('learning_steps_desc')}
        right={
          <StepPills
            value={effective.learningStepsMinutes}
            onChange={(v) => set('learningStepsMinutes', v)}
          />
        }
      />

      <Row
        label={t('relearning_steps')}
        description={t('relearning_steps_desc')}
        right={
          <StepPills
            value={effective.relearningStepsMinutes}
            onChange={(v) => set('relearningStepsMinutes', v)}
          />
        }
      />

      <Row
        label={t('graduating_interval')}
        description={t('graduating_interval_desc')}
        right={
          <Stepper
            value={effective.graduatingIntervalDays}
            min={1}
            max={365}
            suffix={t('days')}
            onChange={(v) => set('graduatingIntervalDays', v)}
          />
        }
      />

      <Row
        label={t('easy_interval')}
        description={t('easy_interval_desc')}
        right={
          <Stepper
            value={effective.easyIntervalDays}
            min={1}
            max={365}
            suffix={t('days')}
            onChange={(v) => set('easyIntervalDays', v)}
          />
        }
      />

      <Row
        label={t('leech_threshold')}
        description={t('leech_threshold_desc')}
        right={
          <Stepper
            value={effective.leechThreshold}
            min={2}
            max={99}
            onChange={(v) => set('leechThreshold', v)}
          />
        }
      />

      <Row
        label={t('leech_action')}
        description={t('leech_action_desc')}
        right={
          <Select<'suspend' | 'tag'>
            size="sm"
            value={effective.leechAction}
            onValueChange={(v) => set('leechAction', v)}
            options={[
              { value: 'suspend', label: t('leech_suspend') },
              { value: 'tag', label: t('leech_tag') },
            ]}
            triggerStyle={{ minWidth: 140 }}
          />
        }
      />

      <Row
        label={t('bury_siblings')}
        description={t('bury_siblings_desc')}
        right={
          <Toggle
            checked={effective.buryNoteSiblings}
            onChange={(v) => set('buryNoteSiblings', v)}
          />
        }
      />

      <div
        style={{
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
          paddingTop: 8,
          borderTop: `1px solid ${colors.line}`,
        }}
      >
        <Button
          variant="secondary"
          disabled={!dirty || updateConfig.isPending}
          onClick={() => setDraft(null)}
        >
          {t('cancel')}
        </Button>
        <Button disabled={!dirty || updateConfig.isPending} onClick={save}>
          {updateConfig.isPending ? '…' : t('save')}
        </Button>
      </div>
    </div>
  );
}

const defaults: ApiDeckConfig = {
  rootPageId: '',
  maxNewPerDay: 20,
  maxReviewsPerDay: 200,
  learningStepsMinutes: [1, 10],
  relearningStepsMinutes: [10],
  graduatingIntervalDays: 1,
  easyIntervalDays: 4,
  leechThreshold: 8,
  leechAction: 'suspend',
  buryNoteSiblings: true,
  scheduler: 'fsrs',
  fsrsRetention: 0.9,
};

function BillingSection() {
  const { t } = useTranslation('settings');
  const plansQuery = useBillingPlans();
  const meQuery = useBillingMe();
  const subQuery = useSubscription();
  const checkout = useCheckout();
  const plan = plansQuery.data?.[0];
  const me = meQuery.data;
  const sub = subQuery.data;
  const isPro = sub?.status === 'active';

  const startCheckout = async () => {
    if (!plan) return;
    const res = await checkout.mutateAsync({
      productId: plan.productId,
      returnUrl: window.location.href,
      cancelUrl: window.location.href,
    });
    window.location.href = res.url;
  };

  return (
    <Section id="billing" title={t('billing_title')} description={t('billing_desc')}>
      <Row
        label={t('plan')}
        description={isPro ? t('plan_active') : t('plan_free')}
        right={
          isPro ? (
            <span style={activePill}>ACTIVE</span>
          ) : (
            <Button
              disabled={!me?.dodoCustomerId || !plan || checkout.isPending}
              onClick={startCheckout}
            >
              {checkout.isPending ? '…' : t('upgrade')}
            </Button>
          )
        }
      />
      <div style={{ height: 1, background: colors.line, margin: '14px 0' }} />
      <Row
        label={t('credits')}
        description={t('credits_desc')}
        right={
          <span
            style={{
              fontVariantNumeric: 'tabular-nums',
              fontSize: 18,
              fontWeight: 500,
              color: (me?.credits ?? 0) > 0 ? colors.ink : colors.ink3,
            }}
          >
            {meQuery.isLoading ? '…' : (me?.credits ?? 0).toLocaleString()}
          </span>
        }
      />
      {!me?.dodoCustomerId && (
        <div style={{ color: colors.ink3, fontSize: 12.5, marginTop: 12 }}>
          {t('no_billing_account')}
        </div>
      )}
    </Section>
  );
}

function ShortcutsSection() {
  const { t } = useTranslation('settings');
  const groups: { title: string; rows: [string, string][] }[] = [
    {
      title: t('shortcuts_review'),
      rows: [
        ['Space', t('kbd_reveal')],
        ['1 / 2 / 3 / 4', t('kbd_rate')],
        ['U', t('kbd_undo')],
        ['B', t('kbd_bury')],
        ['-', t('kbd_suspend')],
        ['! @ # $', t('kbd_flag')],
        ['0', t('kbd_flag_clear')],
      ],
    },
    {
      title: t('shortcuts_editor'),
      rows: [
        ['/', t('kbd_slash_menu')],
        ['{{c1::x}}', t('kbd_cloze')],
        ['front -> back', t('kbd_basic')],
        ['q -> a [d1, d2]', t('kbd_mcq')],
      ],
    },
  ];
  return (
    <Section id="shortcuts" title={t('shortcuts_title')}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {groups.map((g) => (
          <div key={g.title}>
            <div
              style={{
                fontSize: 11,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: colors.ink3,
                marginBottom: 10,
              }}
            >
              {g.title}
            </div>
            {g.rows.map(([kbd, label]) => (
              <div
                key={kbd}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '7px 0',
                  borderBottom: `1px solid ${colors.line}`,
                  fontSize: 13,
                }}
              >
                <span style={{ color: colors.ink2 }}>{label}</span>
                <kbd
                  style={{
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: 11,
                    background: 'rgba(24,22,15,0.05)',
                    padding: '2px 7px',
                    borderRadius: 3,
                    fontFamily: 'inherit',
                  }}
                >
                  {kbd}
                </kbd>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Section>
  );
}

function AccountSection() {
  const { t } = useTranslation('settings');
  const clerk = useClerk();
  const navigate = useNavigate();

  const signOut = async () => {
    await clerk.signOut();
    navigate('/sign-in');
  };

  return (
    <Section id="account" title={t('account_title')}>
      <Row
        label={t('sign_out')}
        description={t('sign_out_desc')}
        right={
          <Button variant="secondary" onClick={signOut}>
            {t('sign_out')}
          </Button>
        }
      />
    </Section>
  );
}

function Row({
  label,
  description,
  right,
}: {
  label: string;
  description?: string;
  right: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 24,
        padding: '6px 0',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500, fontSize: 14, color: colors.ink }}>{label}</div>
        {description && (
          <div style={{ color: colors.ink3, fontSize: 12, marginTop: 2 }}>{description}</div>
        )}
      </div>
      <div style={{ flexShrink: 0 }}>{right}</div>
    </div>
  );
}

function Toggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      onClick={() => onChange(!checked)}
      style={{
        width: 34,
        height: 20,
        borderRadius: 10,
        border: 'none',
        background: checked ? colors.accent : colors.line2,
        position: 'relative',
        cursor: 'pointer',
        transition: 'background 0.12s',
        padding: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: checked ? 16 : 2,
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: colors.surface,
          transition: 'left 0.12s',
          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }}
      />
    </button>
  );
}

function navLink(active: boolean): React.CSSProperties {
  return {
    display: 'block',
    padding: '6px 10px',
    borderRadius: 5,
    fontSize: 13,
    color: active ? colors.ink : colors.ink3,
    background: active ? colors.surface2 : 'transparent',
    textDecoration: 'none',
    transition: 'background 0.12s',
  };
}

const activePill: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.1em',
  color: colors.good,
  background: `${colors.good}1a`,
  padding: '3px 9px',
  borderRadius: 3,
};
